// frontend/client/src/FriendsFeedPage.js
import React, { useEffect, useState } from "react";
import "./FriendsFeedPage.css";

function FriendsFeedPage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);

  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [commentInputs, setCommentInputs] = useState({}); // postId -> text

  const load = () => {
    setLoading(true);
    setError(null);

    fetch("/api/friends-feed", { credentials: "include" })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) throw new Error(data.code || data.error || "load_failed");
        setUser(data.user);
        setCsrfToken(data.csrfToken || "");
        setPosts(data.posts || []);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setError("Could not load friends feed");
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = (text || "").trim();
    if (!trimmed && !imageFile) return;
    if (submitting) return;
    if (!csrfToken) return;

    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append("text", trimmed);
    if (imageFile) formData.append("image", imageFile);
    formData.append("csrfToken", csrfToken);

    fetch("/friends-feed", {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then(() => {
        setText("");
        setImageFile(null);
        load();
      })
      .catch((err) => {
        console.error("friends post error", err);
        setError("network_error");
      })
      .finally(() => setSubmitting(false));
  };

  const handleToggleLike = (postId) => {
    if (!csrfToken) return;

    const body = new URLSearchParams({
      postId: String(postId),
      csrfToken,
      returnTo: "/friends-feed",
    }).toString();

    fetch("/like-post", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
    })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) return;
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  likedByMe: data.liked,
                  likeCount:
                    typeof data.likeCount === "number"
                      ? data.likeCount
                      : p.likeCount,
                }
              : p
          )
        );
      })
      .catch((err) => console.error("like error", err));
  };

  const handleDeletePost = (postId) => {
    if (!csrfToken) return;
    if (!window.confirm("Bu postu silmek istediğine emin misin?")) return;

    const body = new URLSearchParams({
      id: String(postId),
      csrfToken,
      returnTo: "/friends-feed",
    }).toString();

    fetch("/delete-post", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
    })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) return;
        setPosts((prev) => prev.filter((p) => p.id !== postId));
      })
      .catch((err) => console.error("delete error", err));
  };

  const handleCommentChange = (postId, value) => {
    setCommentInputs((prev) => ({ ...prev, [postId]: value }));
  };

  const handleSubmitComment = (e, postId) => {
    e.preventDefault();
    if (!csrfToken) return;

    const raw = commentInputs[postId] || "";
    const trimmed = raw.trim();
    if (!trimmed) return;

    const body = new URLSearchParams({
      postId: String(postId),
      comment: trimmed,
      csrfToken,
      returnTo: "/friends-feed",
    }).toString();

    fetch("/comment-post", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
    })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) return;

        setCommentInputs((prev) => ({ ...prev, [postId]: "" }));

        setPosts((prev) =>
          prev.map((p) => {
            if (p.id !== postId) return p;

            const newCount =
              typeof data.commentCount === "number"
                ? data.commentCount
                : p.commentCount + 1;

            const newComment = {
              authorName: data.authorName || "You",
              text: data.text || trimmed,
            };

            return {
              ...p,
              commentCount: newCount,
              comments: [...(p.comments || []), newComment],
            };
          })
        );
      })
      .catch((err) => console.error("comment error", err));
  };

  if (loading) {
    return <div style={{ padding: 24, color: "#fff" }}>Loading...</div>;
  }

  return (
    <div className="container">
      <header className="topbar">
        <div style={{ fontWeight: 700, fontSize: 18 }}>GRADA · Friends feed</div>
        <button
          className="profile-btn"
          type="button"
          onClick={() => (window.location.href = "/react#/profile")}
        >
          <img
            src="/avatar"
            alt=""
            className="avatar"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div>
            <div className="profile-name">{user?.name}</div>
            <div className="profile-sub">{user?.university}</div>
          </div>
        </button>
      </header>

      <section className="card">
        <h2 className="card-title">Share something with your friends</h2>
        {error && <div className="feed-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <textarea
            placeholder="Share a thought just with your friends"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file =
                e.target.files && e.target.files.length > 0
                  ? e.target.files[0]
                  : null;
              setImageFile(file);
            }}
          />
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? "Posting..." : "Post to friends"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => (window.location.href = "/react#/dashboard")}
          >
            Back to main page
          </button>
        </form>
      </section>

      <section>
        {posts.length === 0 ? (
          <div className="no-posts">
            No posts from friends yet. Share something or follow people back to
            see their posts 🤝
          </div>
        ) : (
          posts.map((p) => (
            <div className="post" id={`post-${p.id}`} key={p.id}>
              <div className="post-header">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img
                    src={`/post-author-avatar?email=${encodeURIComponent(
                      p.authorEmail
                    )}`}
                    className="post-avatar"
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div>
                    <div className="post-author">{p.authorName}</div>
                    <div className="post-meta">
                      {p.university} · {p.createdAt}
                    </div>
                  </div>
                </div>

                {p.isOwnPost && (
                  <button
                    type="button"
                    className="post-delete-btn"
                    onClick={() => handleDeletePost(p.id)}
                  >
                    Delete
                  </button>
                )}
              </div>

              <div className="post-body">
                <p>{p.text}</p>
                {p.hasImage && p.imageUrl && (
                  <div className="post-image-wrapper">
                    <img src={p.imageUrl} className="post-image" alt="" loading="lazy" />
                  </div>
                )}
              </div>

              <div className="post-actions">
                <button
                  type="button"
                  className="btn-like"
                  onClick={() => handleToggleLike(p.id)}
                >
                  {(p.likedByMe ? "Unlike" : "Like")} ({p.likeCount})
                </button>
                <div className="comment-count">{p.commentCount} comments</div>
              </div>

              <div className="post-comments">
                <form
                  className="comment-form"
                  onSubmit={(e) => handleSubmitComment(e, p.id)}
                >
                  <input
                    type="text"
                    placeholder="Write a comment..."
                    className="comment-input"
                    value={commentInputs[p.id] || ""}
                    onChange={(e) => handleCommentChange(p.id, e.target.value)}
                  />
                </form>

                {(p.comments || []).map((c, idx) => (
                  <div className="comment-row" key={idx}>
                    <span className="comment-author">
                      {c.authorName || "You"}:
                    </span>
                    <span className="comment-text">{c.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

export default FriendsFeedPage;
